import type {
  BenchProfilePoint,
  SteamBenchPreset,
} from './types';

/** A sauna's profile presets for its sculpted benches: a list of their own,
 *  separate from the steam room's (docs/claude/lattice.md), stored in the same
 *  Baserow table told apart by `kind: 'sauna-profile'`. Same shape as a steam
 *  preset: a closed cross-section, x from the wall, y up from the floor. */
export type SaunaProfilePreset = SteamBenchPreset;

/** The preset a new sculpted bench in a sauna starts with. */
export const DEFAULT_SAUNA_PROFILE_ID = 'sauna-default';

/** A quarter arc's bulge (counter-clockwise). */
const Q = 0.414214;

/** Served when the database holds none: one level, two levels merged into one
 *  body, two levels with a backrest rising from the upper seat, and Default — a
 *  600 × 100 slab, its top front corner rounded 50, laid floating (its top at
 *  the seat height, docs/claude/lattice.md) on trestles. Default is also the
 *  database's row (the user's: the only one stored). */
export const DEFAULT_SAUNA_PROFILE_PRESETS: SaunaProfilePreset[] = [
  {
    id: 'sauna-a', name: 'Sauna A',
    profile: [
      { x: 0, y: 0 }, { x: 600, y: 0 }, { x: 600, y: 410, bulge: Q }, { x: 560, y: 450 }, { x: 0, y: 450 },
    ],
  },
  {
    id: 'sauna-b', name: 'Sauna B',
    profile: [
      { x: 0, y: 0 }, { x: 1100, y: 0 }, { x: 1100, y: 410, bulge: Q }, { x: 1060, y: 450 }, { x: 600, y: 450 },
      { x: 600, y: 860, bulge: Q }, { x: 560, y: 900 }, { x: 0, y: 900 },
    ],
  },
  {
    id: 'sauna-c', name: 'Sauna C',
    profile: [
      { x: 0, y: 0 }, { x: 1100, y: 0 }, { x: 1100, y: 410, bulge: Q }, { x: 1060, y: 450 }, { x: 600, y: 450 },
      { x: 600, y: 860, bulge: Q }, { x: 560, y: 900 }, { x: 160, y: 900 }, { x: 70, y: 1350 }, { x: 0, y: 1350 },
    ],
  },
  {
    id: 'sauna-default', name: 'Default',
    profile: [
      { x: 0, y: 0 }, { x: 600, y: 0 }, { x: 600, y: 50, bulge: Q }, { x: 550, y: 100 }, { x: 0, y: 100 },
    ],
  },
];

/** A sauna profile preset's Baserow `data` blob. */
export function saunaProfileBlob(p: SaunaProfilePreset): Record<string, unknown> {
  return { kind: 'sauna-profile', id: p.id, name: p.name, profile: p.profile };
}

/** A parsed `data` blob back into a sauna profile preset, or null when it is not one. */
export function saunaProfileFromBlob(parsed: unknown, rowId: number): SaunaProfilePreset | null {
  const o = parsed as { kind?: unknown; id?: unknown; name?: unknown; profile?: unknown } | null;
  if (!o || o.kind !== 'sauna-profile' || !o.id || !Array.isArray(o.profile)) return null;
  const profile: BenchProfilePoint[] = (o.profile as Array<{ x?: unknown; y?: unknown; bulge?: unknown }>)
    .map((q) => ({ x: Number(q.x), y: Number(q.y), ...(Number(q.bulge) ? { bulge: Number(q.bulge) } : {}) }))
    .filter((q) => Number.isFinite(q.x) && Number.isFinite(q.y));
  if (profile.length < 3) return null;
  return { rowId, id: String(o.id), name: String(o.name ?? o.id), profile };
}
